import { createContext, useContext, type ReactNode } from "react";
import type { Role } from "@/api/types";
import type { RuntimeState } from "@/lib/status";

export interface ShellRuntime {
  state: RuntimeState;
  version?: string;
  apiVersion?: string;
  engineVersion?: string;
  instanceId?: string;
  platform?: string;
  ocPath?: string;
  ocCommit?: string;
  dockerVersion?: string;
  lastCheckAt?: string;
  degradedReason?: string;
  required?: string;
}

export interface ShellUser {
  name: string;
  email: string;
  picture?: string;
  role?: Role;
}

export interface ShellData {
  runtime: ShellRuntime;
  user: ShellUser | null;
  activeRun: { runId: string; done: number; total: number } | null;
  pendingCount: number;
  adminApi: "online" | "unreachable";
  cachedRoleMinutesLeft: number;
  online: boolean;
  signOut: () => void;
  runEnvironmentCheck: () => void;
  retryRuntime: () => void;
}

export const DEFAULT_SHELL_DATA: ShellData = {
  runtime: { state: "connected", version: "0.1.0", apiVersion: "v1" },
  user: null,
  activeRun: null,
  pendingCount: 0,
  adminApi: "online",
  cachedRoleMinutesLeft: 5,
  online: true,
  signOut: () => undefined,
  runEnvironmentCheck: () => undefined,
  retryRuntime: () => undefined,
};

const Ctx = createContext<ShellData>(DEFAULT_SHELL_DATA);

export function ShellDataProvider({ value, children }: { value: ShellData; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useShellData(): ShellData {
  return useContext(Ctx);
}
